import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetMyLogbooksQuery, useGetMyLogbookResultLetterQuery } from "../api/logbookApi";
import { useAuth } from "../../../context/AuthContexts";
import sgSignature from "../../../assets/sgsignature.jpeg";

function formatDate(dateLike?: string | null) {
  if (!dateLike) return "—";
  return new Date(dateLike).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function LogbookResultPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: logbooks = [], isLoading } = useGetMyLogbooksQuery();

  const logbook = useMemo(
    () => logbooks.find((x: any) => String(x.id) === String(id)),
    [logbooks, id],
  );

  // The letter is worded server-side, from the diet's own settings and for this
  // candidate's actual outcome. It matters that this page does NOT compose the
  // wording itself: it used to, and what it composed congratulated everyone.
  //
  // A legacy logbook has no diet, so the API answers with the default wording —
  // the exact text this page has always printed — and nothing about an existing
  // result changes.
  const { data: letter } = useGetMyLogbookResultLetterQuery(id!, { skip: !id });
  // console.log(logbook, "the real users *********************");
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
        Loading...
      </div>
    );
  }

  if (!logbook) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        Logbook not found.
      </div>
    );
  }

  // New assessment-diet results are withheld until the board approves them.
  // Legacy logbooks (no assessment diet) are unaffected.
  const withheld =
    !!logbook?.logbook_assessment_diet_id && !logbook?.result_approved_at;
  if (withheld) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white border rounded-2xl p-8 shadow-sm">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 grid place-items-center mx-auto mb-4">
            <span className="text-2xl">⏳</span>
          </div>
          <h2 className="text-lg font-bold text-gray-800">Result pending approval</h2>
          <p className="text-sm text-gray-500 mt-2">
            Your logbook has been assessed. The result will be available here once
            it has been approved by the board.
          </p>
          <button
            onClick={() => navigate("/logbook")}
            className="mt-5 px-4 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50"
          >
            Back to my logbooks
          </button>
        </div>
      </div>
    );
  }
  // console.log(logbook, "this is ehte log boodk@@@@@@@@@@@@@@@@@@@@@@");
  const year = new Date(logbook?.cert_issued_date).getFullYear();

  const reference =
    logbook.reference || `NIQS/EB/${user?.membership_no}/${year}LB`;
  const assessedDate = logbook?.cert_issued_at;
  const dietTitle = logbook?.session || "—";
  const stage = logbook.stage ?? 1;
  const nextStage = Number(stage) + 1;
  const memberName = (user?.name || "—").toUpperCase();

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #logbook-result-letter,
          #logbook-result-letter * { visibility: visible; }
          #logbook-result-letter {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            box-shadow: none;
          }
        }
      `}</style>
      {/* Action buttons — hidden when printing */}
      <div className="max-w-3xl mx-auto flex gap-3 mb-6 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-semibold bg-white hover:bg-gray-50 transition"
        >
          Back
        </button>
        <button
          onClick={() => window.print()}
          className="px-5 py-2 bg-[#1a3a6b] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition"
        >
          Print Statement
        </button>
      </div>

      {/* Letter */}
      <div
        id="logbook-result-letter"
        className="max-w-3xl mx-auto bg-white shadow-md print:shadow-none print:max-w-none"
        style={{ fontFamily: "Times New Roman, serif" }}
      >
        {/* Letterhead */}
        <div className="flex items-start justify-between border-b-2 border-[#1a3a6b] px-10 pt-8 pb-5">
          <img
            src="/niqs-logo.png"
            alt="NIQS Logo"
            className="h-20 object-contain"
          />
          <div className="text-right text-[11px] text-gray-600 leading-relaxed mt-1">
            <p className="font-bold text-[#1a3a6b] text-sm">
              NIGERIAN INSTITUTE OF QUANTITY SURVEYORS
            </p>
            <p className="italic text-[10px] text-gray-500 mb-1">
              The professional construction cost managers
            </p>
            <p>No. 24, NIQS Crescent, Off Michael Ama Nnachi Crescent,</p>
            <p>Cadastral Zone B6, Mabushi District, Abuja.</p>
            <p>P.O. Box 10089 Garki Abuja.</p>
            <p>Tel: +234 (0) 816 759 3807, +234 (0) 802 830 3346</p>
            <p>info@niqs.org.ng | www.niqs.org.ng</p>
          </div>
        </div>

        {/* Reference & Date */}
        <div className="flex justify-between items-start px-10 pt-6 pb-2 text-sm">
          <p className="font-semibold">{reference}</p>
          <p>{formatDate(assessedDate)}</p>
        </div>

        {/* Body */}
        <div className="px-10 pb-10 text-sm leading-relaxed space-y-4">
          {/* Member name */}
          <p className="font-bold text-base pt-2">{memberName}</p>

          <p>Dear Sir/Ma,</p>

          {/* Subject */}
          <p className="font-bold underline uppercase text-center">
            {letter?.subject
              ?? `PROFESSIONAL EXAMINATION: ASSESSMENT OF LOGBOOK AND DIARIES (${dietTitle.toUpperCase()})`}
          </p>

          {/* Body — one paragraph per blank line of the configured wording.
              Falls back to the original text only while the letter is still
              loading, so the page never flashes a congratulation at someone
              who did not pass. */}
          {(letter?.body
            ? String(letter.body).split(/\n\s*\n/)
            : [
                `We write to notify you that you were successful in the assessment of your `
                  + `Professional Work Experience (PWE) as contained in the Logbook and Diary you `
                  + `submitted to the Institute (Stage ${stage}).`,
                Number(stage) >= 3
                  ? "You have successfully completed all stages of the Professional Work Experience assessment."
                  : `You can now proceed to the next stage (Stage ${nextStage}).`,
                "Congratulations.",
              ]
          ).map((para: string, i: number) => (
            <p key={i} style={{ textAlign: "justify" }} className="whitespace-pre-line">
              {para}
            </p>
          ))}

          {/* Sign-off */}
          <div className="pt-4">
            <p>Yours faithfully,</p>
            <p className="font-bold">
              For: The Nigerian Institute of Quantity Surveyors
            </p>

            {/* Signature */}
            <img
              src={sgSignature}
              alt="Secretary General Signature"
              className="h-16 object-contain my-1"
            />

            <p className="font-bold">{letter?.signatory_name ?? "QS Ahmad Kabir Muhammad,FNIQS"}</p>
            <p>{letter?.signatory_title ?? "Secretary General"}</p>
          </div>
        </div>

        {/* Print-only footer buttons */}
        <div className="hidden print:flex justify-center gap-4 border-t py-4 px-10">
          <span className="px-4 py-1 border border-gray-400 text-xs">
            Print Statement
          </span>
          <span className="px-4 py-1 border border-gray-400 text-xs">Back</span>
        </div>
      </div>
    </div>
  );
}
